import { useState, useEffect, useCallback } from 'react';
import { Gathering, GatheringStatus } from '../../../../types';
import gatheringDataService from '../../../../services/data/gatheringService';
import { getGatheringsByStatus, isStatusLoading, isStatusDataStale } from '../../../../services/data/selectors';
import { subscribe } from '../../../../services/data/store';
import { isAbortError } from '../../../../services/api/utils/concurrencyLimit';

export const useGatheringsByStatus = (status: GatheringStatus, token: string | null) => {
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [forceUpdate, setForceUpdate] = useState<number>(0);

  // Set token in the data service
  useEffect(() => {
    gatheringDataService.setToken(token);
  }, [token]);

  // Subscribe to store changes
  useEffect(() => {
    const unsubscribe = subscribe(() => {
      setForceUpdate(prev => prev + 1);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Load data with SWR pattern
  const loadData = useCallback(async (background: boolean = false) => {
    if (!token) return;

    // Declare the variable outside the try/catch so it's accessible in finally
    let firstPageResponse: any = null;

    try {
      if (background) {
        setIsBackgroundLoading(true);
      } else {
        setIsInitialLoading(true);
      }

      // Load the first page
      firstPageResponse = await gatheringDataService.loadGatheringsByStatus(status, 1);
      setError(null);
      
      // Update UI state to show first page immediately
      if (!background) {
        setIsInitialLoading(false);
      }

      // If there are more pages, load them in the background.
      // No local per-status batching: gatheringDataService routes every
      // page through the shared 3-wide limiter at the endpoint layer
      // (see src/services/api/utils/concurrencyLimit.ts), so all pages
      // across all statuses contend for the same 3 slots.
      if (firstPageResponse.totalPages > 1) {
        const additionalPagePromises = [];
        for (let page = 2; page <= firstPageResponse.totalPages; page++) {
          additionalPagePromises.push(
            gatheringDataService.loadGatheringsByStatus(status, page)
              .catch(error => {
                if (isAbortError(error)) {
                  // Filter changes deliberately abort queued pages — quiet,
                  // expected, not an error condition.
                  console.debug(
                    `Cancelled page ${page} load for ${status} gatherings`
                  );
                } else {
                  console.error(
                    `Error loading page ${page} for ${status}:`,
                    error
                  );
                }
                // Return empty response so sibling pages can continue.
                return {
                  gatherings: [],
                  totalCount: 0,
                  totalPages: 0,
                  currentPage: page,
                  hasMore: false
                };
              })
          );
        }
        await Promise.all(additionalPagePromises);
      }
    } catch (err) {
      if (isAbortError(err)) {
        // Filter changes abort the page-1 fetch too; clear the spinner
        // and bail without flagging an error in the UI.
        console.debug(`Cancelled initial load for ${status} gatherings`);
      } else {
        setError(`Failed to load ${status} gatherings`);
        console.error(`Error loading ${status} gatherings:`, err);
      }
    } finally {
      if (background) {
        setIsBackgroundLoading(false);
      } else if (firstPageResponse?.totalPages <= 1) {
        // Only set isInitialLoading to false here if we haven't already done it
        // and if there was only one page to load
        setIsInitialLoading(false);
      }
    }
  }, [token, status]);

  // Load more data for pagination - this is kept for backward compatibility
  // but should rarely be needed since we're loading all pages automatically
  const loadMore = useCallback(async () => {
    if (!token) return;

    try {
      setIsBackgroundLoading(true);

      // Get current pagination state
      const currentPage = gatheringDataService.loadGatheringsByStatus(status, 1)
        .then(response => response.currentPage);

      await gatheringDataService.loadGatheringsByStatus(status, (await currentPage) + 1);
      setError(null);
    } catch (err) {
      setError(`Failed to load more ${status} gatherings`);
      console.error(`Error loading more ${status} gatherings:`, err);
    } finally {
      setIsBackgroundLoading(false);
    }
  }, [token, status]);

  // Initial data loading
  useEffect(() => {
    if (!token) return;

    // Check if we have data and if it's stale
    const hasData = getGatheringsByStatus(status).length > 0;
    const isStale = isStatusDataStale(status);

    if (!hasData) {
      // No data, do a normal load
      loadData(false);
    } else if (isStale) {
      // Data is stale, do a background refresh
      loadData(true);
      setIsInitialLoading(false);
    } else {
      // Data is fresh, no need to load
      setIsInitialLoading(false);
    }
  }, [token, status, loadData]);

  // Set up revalidation triggers
  useEffect(() => {
    if (!token) return;

    // Revalidate on window focus
    const handleFocus = () => {
      if (isStatusDataStale(status)) {
        loadData(true);
      }
    };

    // Revalidate on reconnect
    const handleOnline = () => {
      loadData(true);
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, [token, status, loadData]);

  // Get gatherings for this status from the store
  const gatherings = getGatheringsByStatus(status);

  return {
    gatherings,
    isLoading: isInitialLoading,
    isBackgroundLoading,
    error,
    loadMore,
    refresh: () => loadData(true)
  };
};